/* This component is rendered inside EstimatorPage and is ONLY visible when printing.
   Provides a clean A4-style invoice layout with logo, business details, customer details,
   line items table and totals. The rest of the page is hidden via @media print rules. */
import { TRADES } from '@/lib/calculators';

export default function PrintableInvoice({
  user, logoUrl,
  quoteNumber, name, trade, customer,
  items, itemsTotal,
  labourHours, labourRate, labourDays, labourDayRate, labourCost,
  extraMaterials, materialsCost, subtotal, marginAmt, vatAmt, total, vat, margin,
  notes, showMargin = false,
  template = 'detailed',
  paymentTerms = 'Payment due within 14 days of invoice unless otherwise agreed.',
  quoteValidityDays = 30,
  terms = '',
}) {
  const today = new Date().toLocaleDateString('en-GB');
  const tradeName = TRADES.find((t) => t.slug === trade)?.name || trade;
  const gbp = (n) => `£${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const accent = user?.brand_color || undefined;

  return (
    <div className="print-only" data-testid="printable-invoice">
      <div className="print-page">
        <header className="flex items-start justify-between border-b border-slate-200 pb-4">
          <div className="flex items-start gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="max-h-20 max-w-[160px] object-contain"/>
            ) : null}
            <div>
              <p className="font-display font-semibold text-2xl uppercase tracking-tight">
                {user?.business_name || user?.name || 'TradesMate User'}
              </p>
              {user?.business_address && (
                <p className="whitespace-pre-line text-sm text-slate-700 mt-1">{user.business_address}</p>
              )}
              <p className="text-sm text-slate-700 mt-1">
                {user?.business_phone && <span>{user.business_phone}</span>}
                {user?.business_phone && user?.business_email && <span> · </span>}
                {user?.business_email && <span>{user.business_email}</span>}
              </p>
              {user?.business_vat_number && (
                <p className="text-xs text-slate-600 mt-1">VAT no: {user.business_vat_number}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="font-display font-semibold text-3xl uppercase tracking-tight" style={{ color: accent }}>QUOTE</p>
            {quoteNumber && <p className="font-mono text-sm mt-1">No. {quoteNumber}</p>}
            <p className="font-mono text-xs text-slate-600 mt-1">Issued: {today}</p>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Quote for</p>
            <p className="font-display font-bold text-lg mt-1">{customer?.name || '—'}</p>
            {customer?.address && <p className="text-sm whitespace-pre-line mt-1">{customer.address}</p>}
            {customer?.phone && <p className="text-sm">{customer.phone}</p>}
            {customer?.email && <p className="text-sm">{customer.email}</p>}
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Description</p>
            <p className="font-display font-bold text-lg mt-1">{name || 'Untitled quote'}</p>
          </div>
        </section>

        {items?.filter((it) => (it.description || it.qty || it.unit_price)).length > 0 && (
          <section className="mt-6">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Materials &amp; Line Items</p>
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-y border-slate-200">
                  <th className="text-left py-2 font-display uppercase text-xs tracking-wider">Description</th>
                  <th className="text-right py-2 w-20 font-display uppercase text-xs tracking-wider">Qty</th>
                  <th className="text-left py-2 w-20 font-display uppercase text-xs tracking-wider">Unit</th>
                  {template === 'detailed' && (
                    <>
                      <th className="text-right py-2 w-24 font-display uppercase text-xs tracking-wider">£/unit</th>
                      <th className="text-right py-2 w-28 font-display uppercase text-xs tracking-wider">Total</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {items.filter((it) => (it.description || parseFloat(it.qty) || parseFloat(it.unit_price))).map((it, i) => {
                  const lt = (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0);
                  return (
                    <tr key={i} className="border-b border-slate-300">
                      <td className="py-2">{it.description || '-'}</td>
                      <td className="py-2 text-right font-mono">{it.qty || 0}</td>
                      <td className="py-2 font-mono text-xs">{it.unit || ''}</td>
                      {template === 'detailed' && (
                        <>
                          <td className="py-2 text-right font-mono">{gbp(parseFloat(it.unit_price) || 0)}</td>
                          <td className="py-2 text-right font-mono font-bold">{gbp(lt)}</td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        <section className="grid grid-cols-2 gap-6 mt-6">
          <div>
            {(labourHours > 0 || labourDays > 0 || extraMaterials > 0) && (
              <>
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-semibold">Labour &amp; Extras</p>
                <table className="w-full text-sm">
                  <tbody>
                    {template === 'detailed' ? (
                      <>
                        {labourHours > 0 && (
                          <tr><td>{labourHours} hrs × {gbp(labourRate)}/hr</td><td className="text-right font-mono">{gbp(labourHours * labourRate)}</td></tr>
                        )}
                        {labourDays > 0 && (
                          <tr><td>{labourDays} days × {gbp(labourDayRate)}/day</td><td className="text-right font-mono">{gbp(labourDays * labourDayRate)}</td></tr>
                        )}
                        {extraMaterials > 0 && (
                          <tr><td>Additional materials</td><td className="text-right font-mono">{gbp(extraMaterials)}</td></tr>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Customer-facing: show labour as "X days / X hours of labour" only — no rates. */}
                        {labourHours > 0 && (
                          <tr><td className="py-1">{labourHours} {labourHours === 1 ? 'hour' : 'hours'} of labour included</td></tr>
                        )}
                        {labourDays > 0 && (
                          <tr><td className="py-1">{labourDays} {labourDays === 1 ? 'day' : 'days'} of labour included</td></tr>
                        )}
                        {extraMaterials > 0 && (
                          <tr><td className="py-1">Additional materials included</td></tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </>
            )}
            {notes && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-widest text-slate-500 mb-1 font-semibold">Notes</p>
                <p className="text-sm whitespace-pre-line">{notes}</p>
              </div>
            )}
          </div>

          <div>
            <table className="w-full text-sm">
              <tbody>
                {template === 'detailed' && showMargin ? (
                  <>
                    <tr><td className="py-1 text-slate-600">Materials subtotal</td><td className="text-right font-mono">{gbp(materialsCost)}</td></tr>
                    <tr><td className="py-1 text-slate-600">Labour</td><td className="text-right font-mono">{gbp(labourCost)}</td></tr>
                    <tr className="border-t border-slate-300"><td className="py-1">Subtotal (excl. VAT)</td><td className="text-right font-mono font-bold">{gbp(subtotal)}</td></tr>
                    <tr><td className="py-1 text-slate-600">Margin ({margin}%)</td><td className="text-right font-mono">{gbp(marginAmt)}</td></tr>
                  </>
                ) : (
                  /* Customer-facing (simple template OR detailed-with-margin-hidden): single subtotal with margin silently baked in. */
                  <tr><td className="py-1">Subtotal (excl. VAT)</td><td className="text-right font-mono font-bold">{gbp((subtotal || 0) + (marginAmt || 0))}</td></tr>
                )}
                <tr><td className="py-1 text-slate-600">VAT ({vat}%)</td><td className="text-right font-mono">{gbp(vatAmt)}</td></tr>
                <tr className="border-t border-slate-200"><td className="py-2 font-display font-semibold text-lg uppercase">Total</td><td className="text-right font-mono font-semibold text-xl" style={{ color: accent }}>{gbp(total)}</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <footer className="mt-10 pt-4 border-t border-slate-300 text-xs text-slate-500 font-mono">
          {terms && terms.trim() && (
            <div className="mb-3 text-slate-700">
              <p className="uppercase tracking-widest text-slate-500 mb-1">Terms &amp; Conditions</p>
              <p className="whitespace-pre-line leading-relaxed">{terms}</p>
            </div>
          )}
          <p>Quote valid for {quoteValidityDays} days from issue. {paymentTerms} All estimates are approximate and subject to site inspection.</p>
          <p className="mt-2">Generated with TradesMate UK · tradesmateuk.co.uk</p>
        </footer>
      </div>
    </div>
  );
}
